import QueryString from 'query-string';
import { push } from 'react-router-redux';
import { toastr } from 'react-redux-toastr';
import {
  DEFAULT_DELETE_CONFIG,
  DEFAULT_GET_CONFIG,
  DEFAULT_POST_CONFIG,
  DEFAULT_PUT_CONFIG,
} from '../../config/rest';
import { isErrorResponse } from '../../utils/error-handlers';
import {
  CLEAR_BUS_MAINTENANCE_STAY,
  CLEAR_BUSSES_MAINTENANCE_STAY,
  CLEAR_ITINERARY_ARRIVALS,
  FLAG_BUS_MAINTENANCE_STAY,
  FLAG_GETTING_BUSSES_MAINTENANCE_STAY,
  FLAG_GETTING_ITINERARY_ARRIVALS,
  GET_BUS_MAINTENANCE_STAY,
  GET_BUSSES_MAINTENANCE_STAY,
  GET_ITINERARY_ARRIVALS,
  FLAG_GETTING_BUS_MAINTENANCE_STAY_DEMAND,
  GET_BUS_MAINTENANCE_STAY_DEMAND,
  CLEAR_BUS_MAINTENANCE_STAY_DEMAND,
  FLAG_GETTING_EXTENDED_BUS_MAINTENANCE_STAYS,
  GET_EXTENDED_BUS_MAINTENANCE_STAYS,
  CLEAR_EXTENDED_BUS_MAINTENANCE_STAYS,
} from '../types';
import {
  BUS_MAINTENANCE_STAY_BULK_EDIT_ENDPOINT,
  BUS_MAINTENANCE_STAY_ENDPOINT,
  generateDeleteBusMaintenaceStayPath,
  generateEditBusMaintenaceStayPath,
} from '../../config/endpoints';
import { BUS_MAINTENANCE_STAY_PATH } from '../../config/paths';
import {
  clearMaintenancedBusFleets,
  flagGettingMaintenancedBusFleets,
} from './MaintenanceBusFleet';

const flagBussesMaintenanceStay = (flag) => (dispatch) =>
  dispatch({
    type: FLAG_GETTING_BUSSES_MAINTENANCE_STAY,
    payload: flag,
  });

const getBussesMaintenanceStay =
  async ({ fromDateString, toDateString, cityId }) =>
  async (dispatch) => {
    try {
      dispatch(flagBussesMaintenanceStay(true));
      const query = {
        fromDateString,
        toDateString,
        cityId,
      };
      const url = `${BUS_MAINTENANCE_STAY_ENDPOINT}?${QueryString.stringify(
        query,
      )}`;
      const promise = await fetch(url, DEFAULT_GET_CONFIG);
      await isErrorResponse(promise);
      const response = await promise.json();
      dispatch({
        type: GET_BUSSES_MAINTENANCE_STAY,
        payload: response,
      });
    } catch ({ message }) {
      toastr.error('Error', message);
    } finally {
      dispatch(flagBussesMaintenanceStay(false));
    }
  };

const clearBussesMaintenanceStay = () => (dispatch) =>
  dispatch({
    type: CLEAR_BUSSES_MAINTENANCE_STAY,
  });

const flagBusMaintenanceStayActivity = (flag) => (dispatch) =>
  dispatch({
    type: FLAG_BUS_MAINTENANCE_STAY,
    payload: flag,
  });

const getBusMaintenanceStay =
  async ({ busMaintenanceStayId }) =>
  async (dispatch) => {
    try {
      dispatch(flagBusMaintenanceStayActivity(true));
      const url = `${BUS_MAINTENANCE_STAY_ENDPOINT}/${busMaintenanceStayId}`;
      const promise = await fetch(url, DEFAULT_GET_CONFIG);
      await isErrorResponse(promise);
      const response = await promise.json();
      dispatch({
        type: GET_BUS_MAINTENANCE_STAY,
        payload: response,
      });
    } catch ({ message }) {
      toastr.error('Error', message);
    } finally {
      dispatch(flagBusMaintenanceStayActivity(false));
    }
  };

const clearBusMaintenanceStay = () => (dispatch) =>
  dispatch({
    type: CLEAR_BUS_MAINTENANCE_STAY,
  });

const postBusMaintenanceStay =
  async ({ busMaintenanceStayList }) =>
  async (dispatch) => {
    try {
      dispatch(flagBusMaintenanceStayActivity(true));

      const payload = busMaintenanceStayList.map((busMaintenanceStay) => ({
        ...busMaintenanceStay,
      }));

      const url = BUS_MAINTENANCE_STAY_ENDPOINT;
      const promise = await fetch(url, {
        ...DEFAULT_POST_CONFIG,
        body: JSON.stringify(payload),
      });
      await isErrorResponse(promise);
      const response = await promise.json();
      if (response) {
        dispatch(push(`${BUS_MAINTENANCE_STAY_PATH}`));
      }
    } catch ({ message }) {
      toastr.error('Error', message);
    } finally {
      dispatch(flagBusMaintenanceStayActivity(false));
    }
  };

const putBusMaintenanceStay =
  async (
    busMaintenanceStayId,
    {
      registeredBusId,
      originCityId,
      maintenanceStayStatus,
      estimatedArrivalDate,
      estimatedCompletionDate,
      observation,
    },
  ) =>
  async (dispatch) => {
    try {
      dispatch(flagBusMaintenanceStayActivity(true));
      const payload = {
        id: busMaintenanceStayId,
        registeredBusId,
        originCityId,
        maintenanceStayStatus,
        estimatedArrivalDate,
        estimatedCompletionDate,
        observation,
      };
      const url = generateEditBusMaintenaceStayPath(busMaintenanceStayId);
      const promise = await fetch(url, {
        ...DEFAULT_PUT_CONFIG,
        body: JSON.stringify(payload),
      });
      await isErrorResponse(promise, null, dispatch);
      const busMaintenanceStay = await promise.json();
      dispatch(clearBussesMaintenanceStay());
      toastr.success(
        'Exito',
        'La estadía de mantenimiento del bus ha sido actualizada.',
      );
      return busMaintenanceStay;
    } catch ({ message }) {
      toastr.error('Error', message);
    } finally {
      dispatch(flagBusMaintenanceStayActivity(false));
    }
    return null;
  };

const deleteBusMaintenanceStay =
  async ({ busMaintenanceStayId }) =>
  async (dispatch) => {
    try {
      dispatch(flagBussesMaintenanceStay(true));
      dispatch(flagBusMaintenanceStayActivity(true));
      const url = generateDeleteBusMaintenaceStayPath(busMaintenanceStayId);
      const promise = await fetch(url, {
        ...DEFAULT_DELETE_CONFIG,
      });
      await isErrorResponse(promise, null, dispatch);
      const busMaintenanceStay = await promise.json();
      toastr.success(
        'Exito',
        'La estadía de mantenimiento del bus ha sido eliminada.',
      );
      return busMaintenanceStay;
    } catch ({ message }) {
      toastr.error('Error', message);
    } finally {
      dispatch(flagBusMaintenanceStayActivity(false));
      dispatch(flagBussesMaintenanceStay(false));
    }
    return null;
  };

const flagItineraryArrivals = (flag) => (dispatch) =>
  dispatch({
    type: FLAG_GETTING_ITINERARY_ARRIVALS,
    payload: flag,
  });

const getItineraryArrivals =
  async ({ itineraryDirection, fromDateString, toDateString }) =>
  async (dispatch) => {
    try {
      dispatch(flagItineraryArrivals(true));
      const query = QueryString.stringify({
        itineraryDirection,
        fromDateString,
        toDateString,
      });
      const url = `${BUS_MAINTENANCE_STAY_ENDPOINT}/itinerary-arrivals?${query}`;
      const promise = await fetch(url, DEFAULT_GET_CONFIG);
      await isErrorResponse(promise);
      const response = await promise.json();
      dispatch({
        type: GET_ITINERARY_ARRIVALS,
        payload: response,
      });
    } catch ({ message }) {
      toastr.error('Error', message);
    } finally {
      dispatch(flagItineraryArrivals(false));
    }
  };

const clearItineraryArrivals = () => (dispatch) =>
  dispatch({
    type: CLEAR_ITINERARY_ARRIVALS,
  });

const getMaintenanceStayWithoutArrivalDate = () => async (dispatch) => {
  try {
    dispatch(flagBussesMaintenanceStay(true));
    const url = `${BUS_MAINTENANCE_STAY_ENDPOINT}/without-arrival-date`;
    const promise = await fetch(url, DEFAULT_GET_CONFIG);
    await isErrorResponse(promise);
    const response = await promise.json();
    dispatch({
      type: GET_BUSSES_MAINTENANCE_STAY,
      payload: response,
    });
  } catch (error) {
    toastr.error('Error', error.message);
  } finally {
    dispatch(flagBussesMaintenanceStay(false));
  }
};

const getBusMaintenanceStayConflicts =
  async ({ itineraryId, busId }) =>
  async (dispatch) => {
    try {
      dispatch(flagBusMaintenanceStayActivity(true));
      const url = `${BUS_MAINTENANCE_STAY_ENDPOINT}/conflicts/itinerary/${itineraryId}/bus/${busId}`;
      const promise = await fetch(url, DEFAULT_GET_CONFIG);
      await isErrorResponse(promise);
      return await promise.json();
    } catch ({ message }) {
      toastr.error('Error', message);
      return [];
    } finally {
      dispatch(flagBusMaintenanceStayActivity(false));
    }
  };

const flagBusMaintenanceStayDemand = (flag) => (dispatch) =>
  dispatch({
    type: FLAG_GETTING_BUS_MAINTENANCE_STAY_DEMAND,
    payload: flag,
  });

const getBusMaintenanceStayDemand =
  async ({ fromDateString, toDateString }) =>
  async (dispatch) => {
    try {
      dispatch(flagBusMaintenanceStayDemand(true));
      const query = {
        fromDateString,
        toDateString,
      };
      const url = `${BUS_MAINTENANCE_STAY_ENDPOINT}/service-demand?${QueryString.stringify(
        query,
      )}`;
      const promise = await fetch(url, DEFAULT_GET_CONFIG);
      await isErrorResponse(promise);
      const response = await promise.json();
      dispatch({
        type: GET_BUS_MAINTENANCE_STAY_DEMAND,
        payload: response,
      });
    } catch ({ message }) {
      toastr.error('Error', message);
    } finally {
      dispatch(flagBusMaintenanceStayDemand(false));
    }
  };

const clearBusMaintenanceStayDemand = () => (dispatch) =>
  dispatch({
    type: CLEAR_BUS_MAINTENANCE_STAY_DEMAND,
  });

const putBulkUpdateMaintenanceStay =
  ({ deleteBusMaintenanceStayIdList, updateBusMaintenanceStayList }) =>
  async (dispatch) => {
    try {
      dispatch(flagGettingMaintenancedBusFleets(true));
      dispatch(flagBussesMaintenanceStay(true));

      const payload = {
        deleteBusMaintenanceStayIdList,
        updateBusMaintenanceStayList,
      };

      const url = BUS_MAINTENANCE_STAY_BULK_EDIT_ENDPOINT;

      const promise = await fetch(url, {
        ...DEFAULT_PUT_CONFIG,
        body: JSON.stringify(payload),
      });

      await isErrorResponse(promise);

      const response = await promise.json();

      if (response) {
        toastr.success(
          'Exito',
          'Las estancias de mantenimiento han sido actualizadas.',
        );
        dispatch(clearBussesMaintenanceStay());
        dispatch(clearMaintenancedBusFleets());
      }
    } catch ({ message }) {
      toastr.error('Error', message);
    } finally {
      dispatch(flagBussesMaintenanceStay(false));
      dispatch(flagGettingMaintenancedBusFleets(false));
    }
  };

const getBusMaintenanceStaySearch =
  ({ itineraryId, busMaintenanceStayId }) =>
  async (dispatch) => {
    try {
      dispatch(flagBusMaintenanceStayActivity(true));
      const query = QueryString.stringify({
        itineraryId,
        busMaintenanceStayId,
      });
      const url = `${BUS_MAINTENANCE_STAY_ENDPOINT}/search?${query}`;
      const promise = await fetch(url, DEFAULT_GET_CONFIG);
      await isErrorResponse(promise);
      const response = await promise.json();
      dispatch({
        type: GET_BUSSES_MAINTENANCE_STAY,
        payload: response,
      });
    } catch ({ message }) {
      toastr.error('Error', message);
    } finally {
      dispatch(flagBusMaintenanceStayActivity(false));
    }
  };

const flagExtendedBusMaintenanceStays = (flag) => (dispatch) =>
  dispatch({
    type: FLAG_GETTING_EXTENDED_BUS_MAINTENANCE_STAYS,
    payload: flag,
  });

const getExtendedBusMaintenanceStays = () => async (dispatch) => {
  try {
    dispatch(flagExtendedBusMaintenanceStays(true));

    const url = `${BUS_MAINTENANCE_STAY_ENDPOINT}/extended-maintenance`;

    const promise = await fetch(url, DEFAULT_GET_CONFIG);

    await isErrorResponse(promise);

    const response = await promise.json();

    dispatch({
      type: GET_EXTENDED_BUS_MAINTENANCE_STAYS,
      payload: response,
    });

    return response;
  } catch ({ message }) {
    toastr.error('Error', message);

    return [];
  } finally {
    dispatch(flagExtendedBusMaintenanceStays(false));
  }
};

const clearExtendedBusMaintenanceStays = () => (dispatch) =>
  dispatch({
    type: CLEAR_EXTENDED_BUS_MAINTENANCE_STAYS,
  });

export {
  getBusMaintenanceStay,
  clearBusMaintenanceStay,
  postBusMaintenanceStay,
  putBusMaintenanceStay,
  clearBussesMaintenanceStay,
  getBussesMaintenanceStay,
  deleteBusMaintenanceStay,
  getItineraryArrivals,
  clearItineraryArrivals,
  getMaintenanceStayWithoutArrivalDate,
  getBusMaintenanceStayConflicts,
  getBusMaintenanceStayDemand,
  clearBusMaintenanceStayDemand,
  putBulkUpdateMaintenanceStay,
  getBusMaintenanceStaySearch,
  getExtendedBusMaintenanceStays,
  clearExtendedBusMaintenanceStays,
};
